/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {formatPhoneNumber} from '../../utils/phone-utils'

export default function useProfileFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_profile_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_profile_fields.error.required_first_name'
                })
            },
            error: errors[`${prefix}firstName`],
            control,
            variant: 'floatingProfileForm',
            inputProps: {
                maxLength: 50
            }
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_profile_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_profile_fields.error.required_last_name'
                })
            },
            error: errors[`${prefix}lastName`],
            control,
            variant: 'floatingProfileForm',
            inputProps: {
                maxLength: 50
            }
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_profile_fields.label.email.address'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_profile_fields.error.required_email'
                })
            },
            error: errors[`${prefix}email`],
            control,
            variant: 'floatingGrayed'
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_profile_fields.label.phone'
            }),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_profile_fields.error.required_phone'
                }),
                pattern: {
                    value: /^\+44\s?(\d\s?){10,11}$/,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid phone number',
                        id: 'use_address_fields.error.invalid_phone_format'
                    })
                }
            },
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value))
                }
            }),
            control,
            variant: 'floatingProfileForm'
        },
        loyaltyId: {
            name: `${prefix}loyaltyId`,
            label: formatMessage({
                defaultMessage: 'Loyalty number ID',
                id: 'use_profile_fields.label.loyalty'
            }),
            type: 'text',
            defaultValue: '',
            control,
            variant: 'floatingGrayed'
        },
        birthDay: {
            name: `${prefix}birthDay`,
            label: formatMessage({
                defaultMessage: 'Birthday',
                id: 'uuse_profile_fields.label.birthday'
            }),
            defaultValue: '',
            autoComplete: 'birthDay',
            rules: {
                validate: (value) => {
                    if (!value) return true

                    const birthDate = new Date(value)
                    const today = new Date()

                    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))

                    if (age < 14) {
                        return formatMessage({
                            defaultMessage: 'You must be at least 14 years old',
                            id: 'use_registration_fields.error.min_age'
                        })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthDay`],
            control,
        }
    }

    return fields
}
